import { alpha } from '@mui/material/styles';
import { THEME_COLORS } from '../shared/constants/theme';
import { mobileAppGlassCard } from '../components/layout/mobile/mobileAppTheme';
import { isMobileShellLayout } from './mobileShellLayout';
import {
  getDistributionActionButtonSx,
  getDistributionContentPaperSx,
  getDistributionSectionHeaderRowSx,
  getDistributionSectionHeaderLeadSx,
  getDistributionSectionIconBoxSx,
  getDistributionSectionIconFontSize,
  getDistributionSectionPaperSx,
  getDistributionSectionSubtitleSx,
  getDistributionSectionTitleSx,
  getDistributionTableContainerSx,
} from './distributionMobileLayout';

export const CONFIGURATION_ACCENT = '#4ce1b6';

export function isConfigurationMobileLayout() {
  return isMobileShellLayout();
}

export {
  getDistributionContentPaperSx as getConfigurationContentPaperSx,
  getDistributionSectionHeaderRowSx as getConfigurationSectionHeaderRowSx,
  getDistributionSectionHeaderLeadSx as getConfigurationSectionHeaderLeadSx,
  getDistributionSectionIconBoxSx as getConfigurationSectionIconBoxSx,
  getDistributionSectionIconFontSize as getConfigurationSectionIconFontSize,
  getDistributionSectionPaperSx as getConfigurationSectionPaperSx,
  getDistributionSectionSubtitleSx as getConfigurationSectionSubtitleSx,
  getDistributionSectionTitleSx as getConfigurationSectionTitleSx,
  getDistributionTableContainerSx as getConfigurationTableContainerSx,
};

/** Root wrapper when embedded inside ConfigurationPage shell. */
export function getConfigurationEmbeddedRootSx(isMobile = isConfigurationMobileLayout()) {
  return {
    p: isMobile ? 0 : { xs: 0, md: 0 },
  };
}

/** Section action row — stacks on mobile; hides duplicate page title (NotchTabs shows it). */
export function getConfigurationActionHeaderRowSx(isMobile = isConfigurationMobileLayout()) {
  return {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    justifyContent: { xs: 'stretch', sm: 'space-between' },
    alignItems: { xs: 'stretch', sm: 'center' },
    gap: { xs: 1.25, sm: 0 },
    mb: { xs: 2, sm: 3 },
  };
}

export function getConfigurationActionHeaderTitleSx(isMobile = isConfigurationMobileLayout()) {
  return {
    fontWeight: 600,
    fontSize: { xs: '0.95rem', sm: '1.25rem' },
    color: isMobile ? '#f8fafc' : 'inherit',
    display: { xs: 'none', sm: 'block' },
  };
}

export function getConfigurationAddButtonSx(
  themeColor = CONFIGURATION_ACCENT,
  isMobile = isConfigurationMobileLayout(),
) {
  return {
    ...getDistributionActionButtonSx(themeColor, isMobile),
    width: { xs: '100%', sm: 'auto' },
    alignSelf: { xs: 'stretch', sm: 'auto' },
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: isMobile ? 'none' : undefined,
  };
}

/** Glass card for embedded section panels (GST, tax rules, etc.). */
export function getConfigurationEmbeddedPaperSx(isDark, isMobile = isConfigurationMobileLayout()) {
  if (isMobile) {
    return {
      ...mobileAppGlassCard,
      p: { xs: 1.25, sm: 1.5 },
      mb: 1.5,
      bgcolor: 'transparent',
    };
  }
  return {
    p: 3,
    background: isDark
      ? `linear-gradient(165deg, ${THEME_COLORS.SURFACE_ELEVATED_DARK} 0%, ${THEME_COLORS.BACKGROUND_DARK} 100%)`
      : '#ffffff',
    border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
    borderRadius: 2,
    boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : 'none',
  };
}

/** Compact section header inside embedded papers (icon + title + optional action). */
export function getConfigurationEmbeddedSectionHeaderSx(isMobile = isConfigurationMobileLayout()) {
  return {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    justifyContent: 'space-between',
    alignItems: { xs: 'stretch', sm: 'center' },
    gap: { xs: 1.25, sm: 2 },
    mb: { xs: 2, sm: 3 },
  };
}

export function getConfigurationEmbeddedSectionLeadSx(isMobile = isConfigurationMobileLayout()) {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: 1.25, sm: 2 },
    minWidth: 0,
  };
}

export function getConfigurationEmbeddedSectionTitleSx(isMobile = isConfigurationMobileLayout()) {
  return {
    fontWeight: 600,
    fontSize: { xs: '1rem', sm: '1.5rem' },
    color: isMobile ? '#f8fafc' : 'text.primary',
    mb: 0.25,
  };
}

export function getConfigurationEmbeddedSectionSubtitleSx(isMobile = isConfigurationMobileLayout()) {
  return {
    color: isMobile ? 'rgba(148, 163, 184, 0.92)' : 'text.secondary',
    fontSize: { xs: '0.78rem', sm: '0.875rem' },
  };
}

export function getConfigurationEmbeddedIconBoxSx(
  themeColor = CONFIGURATION_ACCENT,
  isMobile = isConfigurationMobileLayout(),
) {
  return {
    width: { xs: 44, sm: 56 },
    height: { xs: 44, sm: 56 },
    borderRadius: '12px',
    bgcolor: alpha(themeColor, 0.1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  };
}

/** Drawer footer — full-width stacked buttons on mobile. */
export function getConfigurationDrawerFooterSx(isMobile = isConfigurationMobileLayout()) {
  return {
    display: 'flex',
    flexDirection: { xs: 'column-reverse', sm: 'row' },
    gap: { xs: 1, sm: 2 },
    justifyContent: { sm: 'flex-end' },
    '& .MuiButton-root': {
      width: { xs: '100%', sm: 'auto' },
      m: 0,
    },
  };
}

export function getConfigurationCardGridSpacing(isMobile = isConfigurationMobileLayout()) {
  return isMobile ? 1.5 : 3;
}

/** Mobile setup progress strip above NotchTabs. */
export function getConfigurationProgressCardSx() {
  return {
    ...mobileAppGlassCard,
    p: 1.25,
    mb: 1.5,
  };
}

export function getConfigurationProgressLabelSx() {
  return {
    color: 'rgba(148, 163, 184, 0.92)',
    fontSize: '0.72rem',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  };
}

export function getConfigurationProgressValueSx(accent = CONFIGURATION_ACCENT) {
  return {
    color: accent,
    fontSize: '0.78rem',
    fontWeight: 700,
  };
}

export function getConfigurationFinanceCardSx(isDark, isMobile = isConfigurationMobileLayout()) {
  if (isMobile) {
    return {
      ...getConfigurationEmbeddedPaperSx(isDark, isMobile),
      height: '100%',
      position: 'relative',
      overflow: 'hidden',
    };
  }
  return {
    p: 3,
    height: '100%',
    background: isDark
      ? `linear-gradient(165deg, ${THEME_COLORS.SURFACE_ELEVATED_DARK} 0%, ${THEME_COLORS.BACKGROUND_DARK} 100%)`
      : '#ffffff',
    border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
    borderRadius: 2,
    position: 'relative',
    overflow: 'hidden',
    boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : 'none',
  };
}

/** Compact embedded panel — no accent top bar, tighter padding. */
export function getConfigurationCompactCardSx(isDark, isMobile = isConfigurationMobileLayout()) {
  if (isMobile) {
    return {
      ...getConfigurationEmbeddedPaperSx(isDark, isMobile),
      p: { xs: 1.25, sm: 1.5 },
      mb: 1.25,
    };
  }
  return {
    p: 2,
    background: isDark
      ? `linear-gradient(165deg, ${THEME_COLORS.SURFACE_ELEVATED_DARK} 0%, ${THEME_COLORS.BACKGROUND_DARK} 100%)`
      : '#ffffff',
    border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e5e7eb'}`,
    borderRadius: 2,
    boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : 'none',
  };
}

/** Neutral outlined inputs for configuration forms (no mint accent border). */
export function getConfigurationTextFieldSx(isDark) {
  const border = isDark ? alpha('#ffffff', 0.14) : alpha('#0f172a', 0.14);
  const borderHover = isDark ? alpha('#ffffff', 0.22) : alpha('#0f172a', 0.22);
  const borderFocus = isDark ? alpha('#ffffff', 0.32) : alpha('#0f172a', 0.36);
  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: 1.5,
      '& fieldset': { borderColor: border },
      '&:hover fieldset': { borderColor: borderHover },
      '&.Mui-focused fieldset': { borderColor: borderFocus, borderWidth: 1 },
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: isDark ? alpha('#ffffff', 0.72) : 'text.secondary',
    },
  };
}

export function getConfigurationCompactSectionTitleSx(isDark) {
  return {
    fontWeight: 700,
    fontSize: '0.8125rem',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: isDark ? alpha('#ffffff', 0.72) : 'text.secondary',
    mb: 1.25,
  };
}

export function getConfigurationNeutralOutlinedButtonSx(isDark) {
  const border = isDark ? alpha('#ffffff', 0.14) : alpha('#0f172a', 0.14);
  const borderHover = isDark ? alpha('#ffffff', 0.24) : alpha('#0f172a', 0.24);
  return {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 1.5,
    borderColor: border,
    color: 'text.primary',
    '&:hover': {
      borderColor: borderHover,
      bgcolor: isDark ? alpha('#ffffff', 0.04) : alpha('#0f172a', 0.03),
    },
  };
}
